import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import {
  Box, Button, Checkbox, Collapse, Divider, HStack, IconButton, Input, Select, SimpleGrid, Switch, Text, Textarea, Tooltip, VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiPlus, FiTrash2, FiChevronDown, FiChevronRight, FiLayers, FiSquare, FiEdit3, FiCopy,
} from 'react-icons/fi';
import useCompanyRnwy from '../../hooks/useCompanyRnwy';
import useRnwyPreview from '../../hooks/useRnwyPreview'; // item 1450
import { parseCfg, sourcePool, money } from './rnwyCfg';
import {
  RnwyVenues, RnwyVenue, emptyVenue, parseVenues, emptySwitchPilot, RNWY_HARDWARE_TIERS,
} from './rnwyVenues';
import RnwySpecialServices, { rnwyAddOnsTotal } from './RnwySpecialServices'; // item 654 — one-time add-ons (Items engine)
// item 654 — the venue catalog grouping + proposal-level keys live with the (pure) scope builder,
// which composes the SOW AUTOMATICALLY (RnwyScopeManager, in ProposalForm). This file is now
// INPUT ONLY — no per-section "Generate scope" buttons; the SOW auto-builds from these inputs.
import {
  PROPOSAL_LEVEL_KEYS, categoryKeyOf, orderedCategories,
} from './rnwyScopeBuilder';

// RNWY 2.0 · P2 — the "answer-once" venue builder. Job-level section (once) + an unbounded list of
// venues; each venue = a system-size pick + a quantity per CFG catalog item. Owns the structured
// object and pushes it (serialized) into the final-form field `rnwyVenuesJson`. The pricing engine
// (P3) reads this × the CFG rate card. Input only — no prices shown here.

const numV = (s: string): number => (s === '' ? 0 : Number(s));

// item 864 (Chad) — a collapsible wrapper for the "Things we control" panel groups: titled by
// SCOPE ("Entire project" vs the venue's name), chevron-toggled. The entire-project group opens
// by default; per-venue groups start collapsed so long venue lists stay scannable.
const CollapsibleGroup: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title, defaultOpen, children,
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <Box>
      <HStack
        as="button"
        type="button"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        mb={open ? 2 : 0}
      >
        {open ? <FiChevronDown /> : <FiChevronRight />}
        <Text fontSize="sm" fontWeight="semibold">{title}</Text>
      </HStack>
      <Collapse in={open} animateOpacity>
        {children}
      </Collapse>
    </Box>
  );
};

CollapsibleGroup.defaultProps = { defaultOpen: false };

// item 862 — the Customer Builder portal feeds a PRICE-STRIPPED catalog (its role can't read
// Settings → RNWY, and must never see the rate card) and flips portalMode: customer-safe copy
// and no one-time-$ editors. Internal callers pass nothing — behavior unchanged.
interface RnwyVenueBuilderProps {
  cfgJsonOverride?: string | null;
  portalMode?: boolean;
}

const RnwyVenueBuilder: React.FC<RnwyVenueBuilderProps> = ({ cfgJsonOverride, portalMode }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const jsonValue = (values as { rnwyVenuesJson?: string | null }).rnwyVenuesJson ?? undefined;
  const { rnwy } = useCompanyRnwy();
  // item 637 — price from THIS proposal's CFG snapshot (the catalog/sizes shown here come from it);
  // a brand-new quote (no snapshot yet) falls back to the live global default until first save.
  // item 862 — the portal's stripped catalog wins outright (the other two 403 for its role).
  const proposalCfgJson = (values as { rnwyCfgJson?: string | null }).rnwyCfgJson;
  const cfg = parseCfg(cfgJsonOverride || proposalCfgJson || rnwy?.cfgJson);
  // item 1450 (Chad/Tom) — each venue's cost in its header: the live engine preview (same POST the
  // Pricing step makes), keyed by venue order. Never in the portal builder (price-stripped, and the
  // endpoint is internal) and never on a manually priced deal (item 954 — the engine is paused).
  const manualPriced = !!(values as { rnwyManualPricingJson?: string | null }).rnwyManualPricingJson;
  const preview = useRnwyPreview(jsonValue ?? '', proposalCfgJson ?? '', !portalMode && !manualPriced);
  const venuePrice = (i: number) => preview?.venues?.[i];

  const [model, setModel] = useState<RnwyVenues>(() => parseVenues(jsonValue));
  const hydrated = useRef<boolean>(!!jsonValue);
  // item 1015 (Tom's hollow submission) — "content" = anything a person actually built:
  // a named venue, any quantity, notes, equipment or source picks.
  const hasContent = (m: RnwyVenues): boolean => m.venues.some((v) => (v.name ?? '').trim() !== ''
    || Object.values(v.quantities ?? {}).some((q) => (q || 0) > 0)
    || Object.values(v.notes ?? {}).some((n) => (n ?? '').trim() !== '')
    || Object.values(v.controlledEquipment ?? {}).some((list) => (list ?? []).length > 0)
    || (v.sources ?? []).length > 0);
  useEffect(() => {
    if (!jsonValue) return;
    if (!hydrated.current) { setModel(parseVenues(jsonValue)); hydrated.current = true; return; }
    // item 1015 — SAVED DATA BEATS A PRISTINE LOCAL MODEL: if the user's only interaction so
    // far is contentless (e.g. the single/multi venue click landed before the draft finished
    // loading, which used to permanently block hydration and let the next save overwrite the
    // real build with an empty one), late-arriving saved venues with content still hydrate.
    const incoming = parseVenues(jsonValue);
    setModel((cur) => (!hasContent(cur) && hasContent(incoming) ? incoming : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonValue]);

  const update = (next: RnwyVenues) => {
    hydrated.current = true;
    setModel(next);
    form.change('rnwyVenuesJson', JSON.stringify(next));
  };

  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.500', 'whiteAlpha.600');
  // item 639 — readable contrast: brighter field labels + category headers on the dark canvas,
  // and a brand accent so a FILLED (non-zero) quantity pops out of the wall of empty fields.
  const labelColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  const groupColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const filledAccent = useColorModeValue('brandGreen.500', 'brandGreen.300');
  // dart (2026-07-16, Chad) — category-header band so groups read as organized sections
  const headerBand = useColorModeValue('gray.100', 'whiteAlpha.200');
  const headerText = useColorModeValue('gray.700', 'whiteAlpha.900');
  const headerAccent = useColorModeValue('brand.500', 'brand.300');
  // item 681 (Chad) — sexier Equipment/Sources chips: a bordered pill per option that lights up in
  // the brand accent when selected. Panel + chip surfaces below the section header.
  const panelBg = useColorModeValue('white', 'whiteAlpha.50');
  const chipBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const chipCheckedBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');

  const sizes = cfg?.systemSizes ?? [];
  const catalog = cfg?.catalog ?? [];
  const defaultSize = sizes.find((s) => s.key === 'md')?.key ?? sizes[0]?.key ?? 'md';

  // item 1185 — which per-line note inputs are toggled open (lines WITH a note always show)
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const setVenue = (i: number, patch: Partial<RnwyVenue>) => update({
    ...model, venues: model.venues.map((v, idx) => (idx === i ? { ...v, ...patch } : v)),
  });
  const setQty = (i: number, key: string, qty: number) => {
    const v = model.venues[i];
    setVenue(i, { quantities: { ...v.quantities, [key]: qty } });
  };
  // item 681 (Chad) — toggle an individual controllable SOURCE on/off for a venue (chosen from the
  // CFG "Sources" pool). These auto-write into the venue's scope.
  const toggleSource = (i: number, source: string) => {
    const v = model.venues[i];
    const cur = v.sources ?? [];
    const next = cur.includes(source) ? cur.filter((x) => x !== source) : [...cur, source];
    setVenue(i, { sources: next });
  };
  // item 681(E) (Chad) — toggle a controlled-equipment product on/off for a venue (category NAME ->
  // selected product names). These auto-write into the venue's scope.
  const toggleEquip = (i: number, catName: string, item: string) => {
    const v = model.venues[i];
    const cur = v.controlledEquipment?.[catName] ?? [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    setVenue(i, { controlledEquipment: { ...(v.controlledEquipment ?? {}), [catName]: next } });
  };
  // item 1041 (Chad) — per-category "Other" free text on the equipment checklists (both the
  // per-venue and entire-project panels, internal + portal). Stored as an "Other: <text>" entry
  // in the category's picks array, so it flows through BOTH scope composers verbatim with zero
  // backend change; informational only — Chad promotes real options in Settings → RNWY → CFG.
  const OTHER_PREFIX = 'Other: ';
  // item 1187 — equipment Other = APPEND too (was replace-the-single-entry)
  const setEquipOther = (i: number, catName: string, text: string | null) => {
    if (!text || !text.trim()) return;
    const v = model.venues[i];
    setVenue(i, { controlledEquipment: { ...(v.controlledEquipment ?? {}), [catName]: [...(v.controlledEquipment?.[catName] ?? []), `${OTHER_PREFIX}${text.trim()}`] } });
  };
  // which categories have their Other input OPEN (keyed `${keyPrefix}|${category}`) — kept as UI
  // state so the input survives while the typed text is momentarily empty
  const [otherOpen, setOtherOpen] = useState<Record<string, boolean>>({});
  // item 1187 (Chad: "what if i have a second other?") — the Other input is an ADDER now:
  // each add appends its own "Other: <text>" entry (the array always supported many);
  // existing customs render as removable chips. Drafts keyed like otherOpen.
  const [otherDraft, setOtherDraft] = useState<Record<string, string>>({});
  const othersOf = (list: string[] | undefined): string[] => (list ?? []).filter((x) => x.startsWith(OTHER_PREFIX));

  // item 647 — guided/sectioned venues: each collapses to a summary header. A venue with no items
  // yet defaults OPEN (fill it); once it has items it defaults collapsed to a clean summary. The
  // chevron overrides either way. Newly added venue opens.
  const [openVenues, setOpenVenues] = useState<Record<number, boolean>>({});
  const venueItemCount = (v: RnwyVenue) => Object.values(v.quantities).filter((q) => (q || 0) > 0).length;
  const isVenueOpen = (i: number) => openVenues[i] ?? (venueItemCount(model.venues[i]) === 0);
  const toggleVenue = (i: number) => setOpenVenues((s) => ({ ...s, [i]: !isVenueOpen(i) }));
  const addVenue = () => {
    const idx = model.venues.length;
    update({ ...model, venues: [...model.venues, emptyVenue(defaultSize)] });
    setOpenVenues((s) => ({ ...s, [idx]: true }));
  };
  // item 863 (Chad) — the guided pre-venue question. MULTI: Venue 1 auto-named "Head End",
  // sized Medium (md), then Add venue like normal. SINGLE: exactly one unnamed venue (name it
  // yourself) and the Add venue button is locked away. Deleting every venue re-asks.
  const startMultiVenue = () => {
    update({
      ...model,
      jobLevel: { ...model.jobLevel, venueMode: 'multi' },
      venues: [{ ...emptyVenue('md'), name: 'Head End' }],
    });
    setOpenVenues({ 0: true });
  };
  const startSingleVenue = () => {
    update({
      ...model,
      jobLevel: { ...model.jobLevel, venueMode: 'single' },
      venues: [emptyVenue(defaultSize)],
    });
    setOpenVenues({ 0: true });
  };
  const removeVenue = (i: number) => update({ ...model, venues: model.venues.filter((_, idx) => idx !== i) });
  // item 1450 (Chad/Tom: "finish a venue and then duplicate a venue… copy over all the same text and
  // quantities, then we change the name") — a deep copy inserted right after the original, named
  // "<name> (copy)", opened for editing. Venues are keyed by position, so nothing else refers to it.
  const duplicateVenue = (i: number) => {
    const src = model.venues[i];
    const copy: RnwyVenue = {
      ...JSON.parse(JSON.stringify(src)) as RnwyVenue,
      name: `${(src.name ?? '').trim() || `Venue ${i + 1}`} (copy)`,
    };
    const venues = [...model.venues.slice(0, i + 1), copy, ...model.venues.slice(i + 1)];
    update({ ...model, venues });
    // shift the open-state map for everything after the insertion point, then open the copy
    setOpenVenues((st) => {
      const next: Record<number, boolean> = {};
      Object.entries(st).forEach(([k, open]) => { const idx = Number(k); next[idx > i ? idx + 1 : idx] = open; });
      next[i + 1] = true;
      return next;
    });
  };

  const jl = model.jobLevel;
  const setJob = (patch: Partial<RnwyVenues['jobLevel']>) => update({ ...model, jobLevel: { ...jl, ...patch } });
  const sp = jl.switchPilot;
  const setSp = (patch: Partial<RnwyVenues['jobLevel']['switchPilot']>) => setJob({ switchPilot: { ...sp, ...patch } });
  const { modules } = jl;
  const setModules = (patch: Partial<RnwyVenues['jobLevel']['modules']>) => setJob({ modules: { ...modules, ...patch } });

  if (!cfg) {
    // item 862 — the portal never mentions CFG/Settings (internal vocabulary)
    return portalMode
      ? <Text fontSize="sm" color={sub}>The system catalog isn’t available right now — please try again in a moment.</Text>
      : <Text fontSize="sm" color={sub}>Set up the CFG rate card in Settings → RNWY first — the venue builder reads its catalog.</Text>;
  }

  // item 681/684 (Chad) — the CFG-driven Equipment (categorized) + Sources (flat) lists, shared by BOTH
  // the per-venue pickers AND the property-wide picker. Blank products filtered out.
  const equipList = (cfg.controlledEquipment ?? [])
    .map((c) => ({ name: c.name, items: (c.items ?? []).filter((x) => (x ?? '').trim()) }))
    .filter((c) => c.items.length > 0);
  const sourceList = sourcePool(cfg);

  // item 684 (Chad) — PROPERTY-WIDE toggles (entered once, shared across every venue).
  const toggleJobEquip = (catName: string, item: string) => {
    const cur = jl.controlledEquipment?.[catName] ?? [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    setJob({ controlledEquipment: { ...(jl.controlledEquipment ?? {}), [catName]: next } });
  };
  const toggleJobSource = (s: string) => {
    const cur = jl.sources ?? [];
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    setJob({ sources: next });
  };
  // item 1186/1187 — sources Other = APPEND (many allowed); removal = chip toggle
  const setSourceOther = (i: number, text: string | null) => {
    if (!text || !text.trim()) return;
    const v = model.venues[i];
    setVenue(i, { sources: [...(v.sources ?? []), `${OTHER_PREFIX}${text.trim()}`] });
  };
  const setJobSourceOther = (text: string | null) => {
    if (!text || !text.trim()) return;
    setJob({ sources: [...(jl.sources ?? []), `${OTHER_PREFIX}${text.trim()}`] });
  };
  // item 1041 → 1187 — property-wide Other appends
  const setJobEquipOther = (catName: string, text: string | null) => {
    if (!text || !text.trim()) return;
    setJob({ controlledEquipment: { ...(jl.controlledEquipment ?? {}), [catName]: [...(jl.controlledEquipment?.[catName] ?? []), `${OTHER_PREFIX}${text.trim()}`] } });
  };

  // item 681 (Chad) — a selectable "chip" (bordered pill that lights up in the brand accent when
  // checked). Shared by the per-venue + property-wide Equipment/Sources pickers.
  const chip = (checked: boolean, label: string, onToggle: () => void, aria: string) => (
    <Box
      key={label}
      borderWidth="1px"
      borderRadius="md"
      borderColor={checked ? filledAccent : border}
      bg={checked ? chipCheckedBg : chipBg}
      px={3}
      py={1.5}
      cursor="pointer"
      transition="border-color 0.12s ease, background-color 0.12s ease"
      onClick={onToggle}
      _hover={{ borderColor: filledAccent }}
    >
      <Checkbox isChecked={checked} pointerEvents="none" size="sm" spacing={3} aria-label={aria} onChange={() => {}}>
        <Text fontSize="sm" color={checked ? headerText : labelColor} fontWeight={checked ? 'semibold' : 'normal'} noOfLines={1} title={label}>{label}</Text>
      </Checkbox>
    </Box>
  );

  // item 681/684 (Chad) — the Equipment + Sources panels, reused for a venue (per-venue picks) or the
  // whole property (job-level picks). `eq`/`src` are the current selections; the toggles mutate them.
  // `excludeEquip`/`excludeSources` hide anything already covered PROPERTY-WIDE, so venues de-dupe
  // against the shared picks (no double entry). Property-wide panels pass no exclude (show everything).
  const equipmentPanel = (
    eq: Record<string, string[]> | undefined,
    onToggle: (catName: string, item: string) => void,
    keyPrefix: string,
    excludeEquip?: Record<string, string[]>,
    scopeLabel?: string, // item 864 — "Entire project" vs the venue's name in the header
    onOther?: (catName: string, text: string | null) => void, // item 1041 — per-category free text
  ) => {
    const cats = equipList
      .map((c) => ({ name: c.name, items: c.items.filter((it) => !(excludeEquip?.[c.name] ?? []).includes(it)) }))
      .filter((c) => c.items.length > 0);
    if (!cats.length) return null;
    return (
      <Box borderWidth="1px" borderColor={border} borderRadius="lg" bg={panelBg} overflow="hidden">
        <Box bg={headerBand} borderLeftWidth="3px" borderLeftColor={headerAccent} px={3} py={2}>
          <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="0.06em" color={headerText}>
            {`Equipment we control${scopeLabel ? ` — ${scopeLabel}` : ''}`}
          </Text>
        </Box>
        <VStack align="stretch" spacing={3} p={3}>
          {cats.map((c) => {
            // item 1041 — the category's current "Other: <text>" entry (if any) + its open state
            const otherKey = `${keyPrefix}|${c.name}`;
            const otherChecked = Boolean(otherOpen[otherKey]); // item 1187 — pure UI toggle
            return (
              <Box key={c.name}>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.04em" color={groupColor} mb={1.5}>{c.name}</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  {c.items.map((it) => chip((eq?.[c.name] ?? []).includes(it), it, () => onToggle(c.name, it), `${keyPrefix} equipment ${c.name} ${it}`))}
                  {/* item 1187 — every custom entry is its own removable chip */}
                  {othersOf(eq?.[c.name]).map((entry) => chip(true, entry.slice(OTHER_PREFIX.length), () => onToggle(c.name, entry), `${keyPrefix} equipment ${c.name} custom ${entry}`))}
                  {onOther && chip(otherChecked, 'Other…', () => setOtherOpen((st) => ({ ...st, [otherKey]: !otherChecked })), `${keyPrefix} equipment ${c.name} other`)}
                </SimpleGrid>
                {onOther && otherChecked && (
                  <HStack mt={2}>
                    <Input
                      size="sm"
                      placeholder="Type the equipment, then Add — repeat for more"
                      value={otherDraft[otherKey] ?? ''}
                      aria-label={`${keyPrefix} equipment ${c.name} other text`}
                      onChange={(e) => setOtherDraft((d) => ({ ...d, [otherKey]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (otherDraft[otherKey] ?? '').trim()) {
                          e.preventDefault();
                          onOther(c.name, (otherDraft[otherKey] ?? '').trim());
                          setOtherDraft((d) => ({ ...d, [otherKey]: '' }));
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      isDisabled={!(otherDraft[otherKey] ?? '').trim()}
                      onClick={() => {
                        onOther(c.name, (otherDraft[otherKey] ?? '').trim());
                        setOtherDraft((d) => ({ ...d, [otherKey]: '' }));
                      }}
                    >
                      Add
                    </Button>
                  </HStack>
                )}
              </Box>
            );
          })}
        </VStack>
      </Box>
    );
  };

  const sourcesPanel = (
    src: string[] | undefined,
    onToggle: (s: string) => void,
    keyPrefix: string,
    excludeSources?: string[],
    scopeLabel?: string, // item 864 — "Entire project" vs the venue's name in the header
    onOther?: (text: string | null) => void, // item 1186 (Chad) — same Other grammar as equipment
  ) => {
    const list = sourceList.filter((s) => !(excludeSources ?? []).includes(s));
    if (!list.length) return null;
    const otherKey = `${keyPrefix}|sources`;
    const otherChecked = Boolean(otherOpen[otherKey]); // item 1187 — pure UI toggle
    return (
      <Box borderWidth="1px" borderColor={border} borderRadius="lg" bg={panelBg} overflow="hidden">
        <Box bg={headerBand} borderLeftWidth="3px" borderLeftColor={headerAccent} px={3} py={2}>
          <Text fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="0.06em" color={headerText}>
            {`Sources included${scopeLabel ? ` — ${scopeLabel}` : ''}`}
          </Text>
        </Box>
        <Box p={3}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            {list.map((s) => chip((src ?? []).includes(s), s, () => onToggle(s), `${keyPrefix} source ${s}`))}
            {/* item 1186/1187 — custom sources: removable chips + an ADDER (many allowed) */}
            {othersOf(src).map((entry) => chip(true, entry.slice(OTHER_PREFIX.length), () => onToggle(entry), `${keyPrefix} source custom ${entry}`))}
            {onOther && chip(otherChecked, 'Other…', () => setOtherOpen((st) => ({ ...st, [otherKey]: !otherChecked })), `${keyPrefix} source other`)}
          </SimpleGrid>
          {onOther && otherChecked && (
            <HStack mt={2}>
              <Input
                size="sm"
                placeholder="Type the source, then Add — repeat for more"
                value={otherDraft[otherKey] ?? ''}
                aria-label={`${keyPrefix} source other text`}
                onChange={(e) => setOtherDraft((d) => ({ ...d, [otherKey]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (otherDraft[otherKey] ?? '').trim()) {
                    e.preventDefault();
                    onOther((otherDraft[otherKey] ?? '').trim());
                    setOtherDraft((d) => ({ ...d, [otherKey]: '' }));
                  }
                }}
              />
              <Button
                size="sm"
                isDisabled={!(otherDraft[otherKey] ?? '').trim()}
                onClick={() => {
                  onOther((otherDraft[otherKey] ?? '').trim());
                  setOtherDraft((d) => ({ ...d, [otherKey]: '' }));
                }}
              >
                Add
              </Button>
            </HStack>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Text fontWeight="semibold" fontSize="sm" mb={1}>RNWY Venues</Text>
      <Text fontSize="xs" color={sub} mb={3}>
        Enter each venue once — system size + the quantities it controls. The pricing engine turns
        these into the three options automatically. Add as many venues as the job needs.
      </Text>

      {/* Job-level section (covers the whole job) */}
      <Box borderWidth="1px" borderColor={border} borderRadius="md" bg={cardBg} p={3} mb={4}>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Proposal level (Entered Once)</Text>
        {/* item 642/651 — removed ONLY the job-level "Extra per-venue branding packages" input:
            branding is a PER-VENUE catalog quantity (custom_branding), entered on each venue.
            High-Availability nodes stay here — entered ONCE, job-wide (the sheet's "Venue 1 only",
            since every job has ≥1 venue). Priced on venue 1 at its ha_node rate by the engine, and
            auto-adds a scope block to the SOW when > 0. */}
        <Box mb={3}>
          <Text fontSize="xs" color={sub} mb={1}>High-Availability nodes (3+)</Text>
          <Input
            size="sm"
            type="number"
            min={0}
            placeholder="0"
            value={jl.haNodes || ''}
            aria-label="HA nodes"
            onChange={(e) => setJob({ haNodes: numV(e.target.value) })}
          />
        </Box>

        {/* item 1040 (Chad) — "Add hardware": pick exactly ONE RNWY box tier + a quantity of
            1/3/5/7. Anything above 1 IS high availability, automatically — the extra units are
            the redundancy. Spec-only (no prices — safe on the blind portal); the pick rides the
            SOW into review, where the priced hardware line is added like always. */}
        <Box mb={3}>
          <Text fontSize="xs" color={sub} mb={1}>RNWY hardware (choose one)</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
            {RNWY_HARDWARE_TIERS.map((t) => chip(
              jl.hardware?.sku === t.sku,
              `${t.label} — ${t.desc}`,
              () => setJob(jl.hardware?.sku === t.sku
                ? { hardware: null }
                : { hardware: { sku: t.sku, qty: jl.hardware?.qty ?? 1 }, ownerFurnishedProcessor: false }),
              `rnwy hardware ${t.sku}`,
            ))}
          </SimpleGrid>
          {/* item 1483 (Tom) — the owner supplies the processor: no RNWY box. Clears any tier pick. */}
          <Box mt={2} maxW={{ base: 'full', md: '33%' }}>
            {chip(
              !!jl.ownerFurnishedProcessor,
              'Owner-furnished processor',
              () => setJob(jl.ownerFurnishedProcessor
                ? { ownerFurnishedProcessor: false }
                : { ownerFurnishedProcessor: true, hardware: null }),
              'owner-furnished processor',
            )}
          </Box>
          {jl.hardware?.sku && (
            <HStack mt={2} spacing={3} align="flex-end">
              <Box>
                <Text fontSize="xx-small" color={labelColor}>Quantity</Text>
                <Select
                  size="sm"
                  maxW="100px"
                  value={jl.hardware.qty}
                  aria-label="rnwy hardware quantity"
                  onChange={(e) => setJob({ hardware: { sku: jl.hardware!.sku, qty: Number(e.target.value) } })}
                >
                  {[1, 3, 5, 7].map((q) => <option key={q} value={q}>{q}</option>)}
                </Select>
              </Box>
              {jl.hardware.qty > 1 && (
                <Text fontSize="xs" color={filledAccent} fontWeight="semibold" pb={1.5}>
                  High availability — automatic
                </Text>
              )}
            </HStack>
          )}
        </Box>
        {/* item 668 — one-time items via the catalog add-ons (full item numbers, group + suggested
            resale). Kept HERE in the job section (and in SwitchPilot) — NOT a separate Items tab.
            item 862 — hidden on the portal: it's a PRICED editor, and the customer builds blind. */}
        {!portalMode && (
          <RnwySpecialServices
            value={jl.misc}
            onChange={(misc) => setJob({ misc })}
          />
        )}

        {/* item 647 — Modules: Yes/No include-exclude toggles (NO pricing effect; shown on the PDF
            as "System includes/excludes"). SwitchPilot=Yes reveals its guided pricing build. */}
        <Divider my={3} borderColor={border} />
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Modules</Text>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3} mb={2}>
          {([['concourse', 'Concourse'], ['aviator', 'Aviator'], ['switchPilot', 'SwitchPilot'], ['thirdParty', 'Third-party integration']] as const).map(([key, label]) => (
            <HStack key={key} justify="space-between" borderWidth="1px" borderColor={border} borderRadius="md" px={3} py={2}>
              <Text fontSize="sm" color={labelColor}>{label}</Text>
              <Switch
                size="sm"
                isChecked={modules[key]}
                onChange={(e) => {
                  const {checked} = e.target;
                  // toggling SwitchPilot OFF excludes it from pricing (zero its counts atomically)
                  if (key === 'switchPilot' && !checked) {
                    setJob({ modules: { ...modules, switchPilot: false }, switchPilot: emptySwitchPilot() });
                  } else {
                    setModules({ [key]: checked });
                  }
                }}
                aria-label={`module ${key}`}
              />
            </HStack>
          ))}
        </SimpleGrid>

        {/* item 789 (Chad) — Third-Party is PRICED: qty × the locked THIRDPARTY-MONTHLY
            item's unit price = its own monthly subscription line (opt in/out at approval,
            like SwitchPilot/Help Desk). Qty appears when the module is toggled on. */}
        {modules.thirdParty && (
          <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3} mt={1}>
            <Text fontSize="xs" fontWeight="semibold" mb={1}>Third-Party integrations</Text>
            <Text fontSize="xx-small" color={sub} mb={2}>
              Monthly add-on: quantity × the THIRDPARTY-MONTHLY catalog item&apos;s unit price.
              Leave 0 to keep Third-Party as a display-only module note.
            </Text>
            <Box maxW="180px">
              <Text fontSize="xx-small" color={labelColor}>Integration quantity</Text>
              <Input
                size="sm"
                type="number"
                min={0}
                value={jl.thirdPartyCount || ''}
                aria-label="Third-party integration quantity"
                onChange={(e) => setJob({ thirdPartyCount: numV(e.target.value) })}
              />
            </Box>
          </Box>
        )}

        {/* item 643 — SwitchPilot pricing hidden until the module is toggled on; item 644 — context. */}
        {modules.switchPilot && (
          <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3} mt={1}>
            <Text fontSize="xs" fontWeight="semibold" mb={1}>
              SwitchPilot pricing
              {sp.switchCount > 0 ? ` · ${sp.switchCount} switch${sp.switchCount === 1 ? '' : 'es'}${sp.brand ? ` · ${sp.brand}` : ''}` : ''}
            </Text>
            <Text fontSize="xx-small" color={sub} mb={2}>
              Subscription add-on with its own contract. Enter the switch count once — the
              SwitchPilot config + monthly on the pricing cards auto-calculate. Nothing to add manually.
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
              <Box>
                <Text fontSize="xx-small" color={labelColor}>Total switches</Text>
                <Input
                  size="xs"
                  type="number"
                  min={0}
                  placeholder="0"
                  value={sp.switchCount || ''}
                  aria-label="switchpilot count"
                  onChange={(e) => setSp({ switchCount: numV(e.target.value) })}
                />
                <Text fontSize="0.6rem" color={sub} mt={0.5} lineHeight="1.1">Enter the total project switches. Base license covers the first 10; switches over 10 price at the tier for your total count, and one-time config is per switch — see the pricing preview below for the tier + totals.</Text>
              </Box>
              <Box>
                <Text fontSize="xx-small" color={labelColor}>Brand / model</Text>
                <Input
                  size="xs"
                  placeholder="e.g. Netgear M4250"
                  value={sp.brand}
                  aria-label="switchpilot brand"
                  onChange={(e) => setSp({ brand: e.target.value })}
                />
                <Text fontSize="0.6rem" color={sub} mt={0.5} lineHeight="1.1">Used in the generated scope.</Text>
              </Box>
            </SimpleGrid>
            {/* item 654 — SwitchPilot one-time config extras via the SAME Items engine as the
                proposal-level add-ons. The rolled-up total syncs into specialFeatures, which the
                engine adds to SwitchPilot's one-time config (OneTimeConfig += SpecialFeatures).
                item 862 — hidden on the portal (priced editor). */}
            {!portalMode && (
              <Box mt={3}>
                <RnwySpecialServices
                  value={sp.specialItems ?? []}
                  onChange={(rows) => setSp({ specialItems: rows, specialFeatures: rnwyAddOnsTotal(rows) })}
                  title="One-time config extras"
                  hint="One-off SwitchPilot configuration or hardware — pick from your Items or add a one-off. The total is added to SwitchPilot's one-time config."
                  subtotalLabel="Extras subtotal (added to SwitchPilot one-time)"
                />
              </Box>
            )}
          </Box>
        )}

        {/* item 684 (Chad) — PROPERTY-WIDE equipment/sources: enter ONCE, shared across every venue
            (for stuff the whole property shares). Per-venue picks below still work for venue-specific gear.
            item 864 (Chad) — retitled "Things we control — Entire project", COLLAPSIBLE and open by
            default; SINGLE-venue builds control parts here ONLY (per-venue panels are hidden). */}
        {(equipList.length > 0 || sourceList.length > 0) && (
          <Box mt={3}>
            <Divider mb={3} borderColor={border} />
            <CollapsibleGroup title="Things we control — Entire project" defaultOpen>
              <Text fontSize="xs" color={sub} mb={2}>
                {jl.venueMode === 'single'
                  ? 'Check everything we control at this venue.'
                  : 'Shared across every venue on this property — check these once instead of per venue. You can still add venue-specific gear on each venue below.'}
              </Text>
              <VStack align="stretch" spacing={3}>
                {equipmentPanel(jl.controlledEquipment, toggleJobEquip, 'property', undefined, 'Entire project', setJobEquipOther)}
                {sourcesPanel(jl.sources, toggleJobSource, 'property', undefined, 'Entire project', setJobSourceOther)}
              </VStack>
            </CollapsibleGroup>
          </Box>
        )}
      </Box>

      {/* item 681 (Chad) — hint: the per-venue Sources checklist only appears once the CFG "Sources"
          pool has entries; the Equipment checklist needs the CFG "Equipment we control" list.
          item 862 — internal-only vocabulary; never shown on the portal. */}
      {!portalMode && sourcePool(cfg).length === 0 && (cfg.controlledEquipment?.length ?? 0) === 0 && (
        <Text fontSize="xs" color={sub} mb={2}>
          Tip: add your Sources and Equipment lists in Settings → RNWY → CFG to get the per-venue
          Sources + Equipment checklists on each venue below.
        </Text>
      )}

      {/* item 863 (Chad, supersedes the 862 portal panel) — the GUIDED pre-venue question, on BOTH
          the internal builder and the portal: multi venue (Venue 1 auto-named "Head End", sized
          Medium, then add venues like normal) vs single venue (exactly one, unnamed — name it
          yourself; Add venue is locked away). Shown whenever the build has no venues yet. */}
      {model.venues.length === 0 && (
        <Box borderWidth="1px" borderStyle="dashed" borderColor={border} borderRadius="md" p={4} mb={3}>
          <Text fontSize="sm" fontWeight="semibold" mb={1}>How many venues does this system serve?</Text>
          <Text fontSize="xs" color={sub} mb={3}>
            Multi-venue starts you with Venue 1 as the HEAD END — the room where the main
            system rack lives (Medium system) — then add the rest. Single-venue builds exactly one.
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
            <Button
              size="sm"
              leftIcon={<FiLayers />}
              colorScheme="brandGreen"
              variant="outline"
              onClick={startMultiVenue}
            >
              Multi venue — start with the Head End
            </Button>
            <Button
              size="sm"
              leftIcon={<FiSquare />}
              variant="outline"
              onClick={startSingleVenue}
            >
              Single venue — just one
            </Button>
          </SimpleGrid>
        </Box>
      )}

      {/* Venues */}
      <VStack align="stretch" spacing={3}>
        {model.venues.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={`venue-${i}`} borderWidth="1px" borderColor={border} borderRadius="md" bg={cardBg} p={3}>
            <HStack mb={2}>
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={isVenueOpen(i) ? `Collapse venue ${i}` : `Expand venue ${i}`}
                icon={isVenueOpen(i) ? <FiChevronDown /> : <FiChevronRight />}
                onClick={() => toggleVenue(i)}
              />
              {/* item 864 (Chad) — the venue name is the row's identity: bigger + bolder */}
              <Input
                size="md"
                fontSize="lg"
                fontWeight="semibold"
                flex="1"
                placeholder={`Venue ${i + 1} name`}
                aria-label={`venue ${i} name`}
                value={v.name}
                onChange={(e) => setVenue(i, { name: e.target.value })}
              />
              <Box w="220px">
                <Select
                  size="sm"
                  value={v.systemSizeKey}
                  aria-label={`venue ${i} system size`}
                  onChange={(e) => setVenue(i, { systemSizeKey: e.target.value })}
                >
                  {sizes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </Select>
              </Box>
              {/* item 1450 — duplicate this venue (not in SINGLE mode, which locks the build to one venue) */}
              {jl.venueMode !== 'single' && (
                <Tooltip label="Duplicate this venue — same items, quantities and notes; rename the copy" openDelay={300}>
                  <IconButton size="sm" variant="ghost" aria-label={`Duplicate venue ${i}`} icon={<FiCopy />} onClick={() => duplicateVenue(i)} />
                </Tooltip>
              )}
              <IconButton size="sm" variant="ghost" aria-label={`Remove venue ${i}`} icon={<FiTrash2 />} onClick={() => removeVenue(i)} />
            </HStack>
            {/* item 1450 (Chad: "total: [one-time] · Licensing: Perpetual · Total: [both]") — this venue's
                share of the engine result, right under its name. Hidden in the portal builder and on a
                manually priced deal; blank until the preview lands. */}
            {venuePrice(i) && (
              <HStack spacing={4} pl={10} mb={1} fontSize="xs" color={sub} flexWrap="wrap">
                <Text as="span">
                  One-time
                  {' '}
                  <Text as="span" fontWeight="semibold" color={labelColor}>{money(venuePrice(i)!.oneTime)}</Text>
                </Text>
                <Text as="span">
                  Licensing (Perpetual)
                  {' '}
                  <Text as="span" fontWeight="semibold" color={labelColor}>{money(venuePrice(i)!.perpetualLicense)}</Text>
                </Text>
                <Text as="span">
                  Total
                  {' '}
                  <Text as="span" fontWeight="semibold" color={labelColor}>{money(venuePrice(i)!.total)}</Text>
                </Text>
              </HStack>
            )}
            {/* item 647 — collapsed → a clean summary; expanded → the grouped inputs */}
            {!isVenueOpen(i) && (
              <Text fontSize="xs" color={sub} pl={10}>
                {venueItemCount(v)}
                {' '}
                item(s) configured — click the chevron to edit
              </Text>
            )}
            <Collapse in={isVenueOpen(i)} animateOpacity>
              {(() => {
                // dart (2026-07-16, Chad) — the per-venue quantity grid mirrors the CFG EXACTLY: same
                // category order (orderedCategories = the CFG's category order) and same item order
                // within a category (catalog array order — both reflect the CFG drag-reorder), and the
                // SAME category names (no hardcoded "Branding"/"Other" — rename in the CFG and it shows
                // here). Proposal-level keys (HA) are dropped (entered once at the deal level); a
                // category with no per-venue items simply doesn't render a quantity block.
                const cats = orderedCategories(cfg);
                const groups = cats
                  .map((cat) => ({
                    name: cat.name,
                    items: catalog.filter((c) => categoryKeyOf(c, cats) === cat.key && !PROPOSAL_LEVEL_KEYS.has(c.key)),
                  }))
                  .filter((g) => g.items.length > 0);
                // dart (2026-07-16, Chad) — match the CFG hierarchy: one item PER ROW (label left,
                // quantity right), vertically, instead of a multi-column box grid.
                const qtyInput = (c: { key: string; label: string }) => {
                  const filled = (v.quantities[c.key] || 0) > 0;
                  // item 1185 (Chad: "(4) Audio Sources — [NOTE: RockBot BGM Player]") — a
                  // per-LINE note that prints on the generated scope. Contextual, not
                  // always-on: the pencil toggles it; a line WITH a note always shows it.
                  const noteKey = `${i}:${c.key}`;
                  const hasNote = (v.notes?.[c.key] ?? '').trim() !== '';
                  const noteVisible = hasNote || openNotes.has(noteKey);
                  return (
                    <Box key={c.key}>
                      <HStack spacing={3} justify="space-between" py={1} px={1}>
                        <Text fontSize="md" color={labelColor} noOfLines={2} title={c.label} lineHeight="1.3" flex="1">{c.label}</Text>
                        <IconButton
                          aria-label={`note for ${c.label}`}
                          icon={<FiEdit3 />}
                          size="sm"
                          variant="ghost"
                          color={hasNote ? filledAccent : undefined}
                          onClick={() => setOpenNotes((prev) => {
                            const n = new Set(prev);
                            if (n.has(noteKey)) n.delete(noteKey); else n.add(noteKey);
                            return n;
                          })}
                        />
                        <Input
                          size="md"
                          type="number"
                          min={0}
                          placeholder="0"
                          w="100px"
                          flexShrink={0}
                          textAlign="right"
                          value={v.quantities[c.key] || ''}
                          aria-label={`venue ${i} ${c.key}`}
                          onChange={(e) => setQty(i, c.key, numV(e.target.value))}
                          borderColor={filled ? filledAccent : undefined}
                          fontWeight={filled ? 'bold' : undefined}
                        />
                      </HStack>
                      {noteVisible && (
                        <Input
                          size="sm"
                          mb={1}
                          ml={1}
                          placeholder="Line note — prints on the scope (e.g. RockBot BGM Player)"
                          aria-label={`venue ${i} ${c.key} note`}
                          value={v.notes?.[c.key] ?? ''}
                          onChange={(e) => setVenue(i, { notes: { ...(v.notes ?? {}), [c.key]: e.target.value } })}
                        />
                      )}
                    </Box>
                  );
                };
                return (
                  <VStack align="stretch" spacing={5}>
                    {groups.map((g) => (
                      <Box key={g.name}>
                        {/* dart (2026-07-16, Chad) — a distinct category band (subtle fill + brand accent
                            stripe) so groups read as organized sections, not one clumped list */}
                        <HStack
                          spacing={2}
                          align="center"
                          mb={1.5}
                          bg={headerBand}
                          borderLeftWidth="3px"
                          borderLeftColor={headerAccent}
                          borderRadius="sm"
                          px={3}
                          py={1.5}
                        >
                          <Text fontSize="md" fontWeight="bold" textTransform="uppercase" letterSpacing="0.06em" color={headerText}>{g.name}</Text>
                        </HStack>
                        <VStack align="stretch" spacing={0.5}>
                          {g.items.map(qtyInput)}
                        </VStack>
                        {/* item 864 (Chad) — a per-SECTION note: a direct line to the reviewing
                            team ("anything we should know about this section?"). Rides the venues
                            JSON (sectionNotes), so Pulse sees it in the internal builder on review. */}
                        <Textarea
                          size="sm"
                          rows={1}
                          mt={1.5}
                          placeholder={`Notes for us about ${g.name.toLowerCase()} (optional)`}
                          aria-label={`venue ${i} ${g.name} notes`}
                          value={v.sectionNotes?.[g.name] ?? ''}
                          onChange={(e) => setVenue(i, { sectionNotes: { ...(v.sectionNotes ?? {}), [g.name]: e.target.value } })}
                        />
                      </Box>
                    ))}
                    {/* item 681/684 (Chad) — per-venue Equipment + Sources (venue-specific gear); anything
                        checked PROPERTY-WIDE above is hidden here so venues de-dupe against the shared picks.
                        item 864 (Chad) — SINGLE-venue builds control parts at the Entire-project level ONLY
                        (this panel hides); multi-venue gets a per-venue collapsible, closed by default. */}
                    {jl.venueMode !== 'single' && (
                      <CollapsibleGroup title={`Things we control — ${v.name || `Venue ${i + 1}`}`}>
                        <VStack align="stretch" spacing={3}>
                          {equipmentPanel(v.controlledEquipment, (cat, it) => toggleEquip(i, cat, it), `venue ${i}`, jl.controlledEquipment, v.name || `Venue ${i + 1}`, (cat, text) => setEquipOther(i, cat, text))}
                          {sourcesPanel(v.sources, (s) => toggleSource(i, s), `venue ${i}`, jl.sources, v.name || `Venue ${i + 1}`, (t) => setSourceOther(i, t))}
                        </VStack>
                      </CollapsibleGroup>
                    )}
                  </VStack>
                );
              })()}
            </Collapse>
          </Box>
        ))}
      </VStack>

      {/* item 863 — the empty state carries the guided chooser above; SINGLE mode locks the
          build to exactly one venue, so Add venue disappears once it exists. */}
      {model.venues.length > 0 && jl.venueMode !== 'single' && (
        <Button mt={3} size="sm" leftIcon={<FiPlus />} onClick={addVenue}>Add venue</Button>
      )}
      {model.venues.length === 0 && (
        <Text fontSize="xs" color={sub} mt={2}>No venues yet — add the first one (it&apos;s the primary/base system).</Text>
      )}
    </Box>
  );
};

RnwyVenueBuilder.defaultProps = { cfgJsonOverride: null, portalMode: false };

export default RnwyVenueBuilder;
